import { formatMoneyCurrency } from './format';

/**
 * Суммы по валютам — общие для финансового дашборда, «Счетов», выплат
 * преподавателя, должников и мини-карточки студента (живёт в `core/utils`,
 * чтобы её брали и организмы). Валюты не складываются: курса нет, суммы показываются разбивкой
 * по валютам («Fix currency mixing on ERP finance dashboard»). Образец —
 * карточка группы (`groupBy: 'currency'`, `core/utils/groupMoney`).
 */

const NBSP = '\u00a0';

/**
 * Сумма с валютой без переноса между числом и кодом: строка из нескольких
 * валют переносится только по « · ».
 */
const moneyNoWrap = (amount: number, currency: string): string =>
  formatMoneyCurrency(amount, currency).replace(/ (?=\S+$)/, NBSP);

/** Главная валюта: в ней сравниваются периоды и рисуются графики. */
export const BASE_CURRENCY = 'PLN';

/** Сумма в одной валюте. */
export interface ICurrencyAmount {
  currency: string;
  amount: number;
  count?: number;
}

/** Строка агрегата счетов, сгруппированного по валюте. */
export interface ICurrencyRow {
  group?: string | null;
  totalGross?: number | null;
}

/** Строка агрегата с суммами плиток (`fakturowniaInvoiceAggregates`). */
export interface IInvoiceCurrencyRow extends ICurrencyRow {
  totalPaid?: number | null;
  totalExpected?: number | null;
  count?: number | null;
}

const codeOf = (raw?: string | null): string =>
  (raw || '').trim().toUpperCase() || BASE_CURRENCY;

/** Код валюты строки агрегата (`group` при `groupBy: 'currency'`). */
export const currencyOf = (row: { group?: string | null }): string =>
  codeOf(row.group);

/**
 * Главная валюта периода: PLN, если в нём есть счета в PLN; иначе валюта с
 * наибольшей суммой выставленного; счетов нет — PLN.
 */
export const primaryCurrency = (rows: ReadonlyArray<ICurrencyRow>): string => {
  if (rows.some((row) => currencyOf(row) === BASE_CURRENCY)) {
    return BASE_CURRENCY;
  }
  const top = [...rows].sort(
    (a, b) => (b.totalGross || 0) - (a.totalGross || 0),
  )[0];
  return top ? currencyOf(top) : BASE_CURRENCY;
};

/** Порядок показа: PLN первой, остальные — по убыванию суммы. */
export const orderAmounts = (
  items: ReadonlyArray<ICurrencyAmount>,
): ICurrencyAmount[] =>
  [...items].sort((a, b) => {
    if (a.currency === b.currency) return 0;
    if (a.currency === BASE_CURRENCY) return -1;
    if (b.currency === BASE_CURRENCY) return 1;
    return b.amount - a.amount;
  });

/** «9025,00 EUR · 100,00 USD»; с `withCount` — «9025,00 EUR (3)». */
export const formatAmounts = (
  items: ReadonlyArray<ICurrencyAmount>,
  withCount = false,
): string =>
  orderAmounts(items)
    .map((item) => {
      const money = moneyNoWrap(item.amount, item.currency);
      return withCount && item.count !== undefined
        ? `${money} (${item.count})`
        : money;
    })
    .join(' · ');

/** Σ по валютам: `{ PLN: 1300, EUR: 9025 }`. */
export const sumByCurrency = <T>(
  rows: ReadonlyArray<T>,
  currency: (row: T) => string | null | undefined,
  amount: (row: T) => number | null | undefined,
): Record<string, number> =>
  rows.reduce<Record<string, number>>((acc, row) => {
    const code = codeOf(currency(row));
    acc[code] = (acc[code] || 0) + (amount(row) || 0);
    return acc;
  }, {});

/**
 * Сводка сумм по валютам одной строкой; пусто — «0,00 PLN» (как было до
 * разбивки).
 */
export const formatCurrencyTotals = (
  totals: Record<string, number>,
): string => {
  const items = Object.entries(totals).map(([currency, amount]) => ({
    currency,
    amount,
  }));
  return items.length ? formatAmounts(items) : moneyNoWrap(0, BASE_CURRENCY);
};

/**
 * Счета вне главной валюты одной строкой с количеством —
 * «9025,00 EUR (3)»; таких нет — пустая строка. Для пояснения «графики — в
 * PLN, счета в других валютах не входят: …».
 */
export const formatOtherCurrencies = <T>(
  rows: ReadonlyArray<T>,
  currency: (row: T) => string | null | undefined,
  amount: (row: T) => number | null | undefined,
): string => {
  const totals = sumByCurrency(rows, currency, amount);
  const counts = rows.reduce<Record<string, number>>((acc, row) => {
    const code = codeOf(currency(row));
    acc[code] = (acc[code] || 0) + 1;
    return acc;
  }, {});
  return formatAmounts(
    Object.keys(totals)
      .filter((code) => code !== BASE_CURRENCY)
      .map((code) => ({
        currency: code,
        amount: totals[code],
        count: counts[code],
      })),
    true,
  );
};

/** Строка расхода: сумма в валюте документа и пересчёт сервера в PLN. */
export interface IPlnConvertible {
  amount?: number | null;
  amountPln?: number | null;
  currency?: string | null;
}

/** Сумма в PLN плюс то, что в PLN пересчитать нечем (нет курса). */
export interface IPlnTotals {
  pln: number;
  /** `{ USD: 300 }` — строки без `amountPln` в валюте не PLN. */
  unconverted: Record<string, number>;
}

/**
 * Σ расходов в PLN без смешения валют: `amountPln` (пересчёт сервера), у
 * PLN-строки без него — `amount`. Строка в другой валюте без курса в PLN не
 * входит — она уходит в `unconverted` по своей валюте.
 */
export const splitPlnTotals = (
  rows: ReadonlyArray<IPlnConvertible>,
): IPlnTotals =>
  rows.reduce<IPlnTotals>(
    (acc, row) => {
      const code = codeOf(row.currency);
      if (row.amountPln !== null && row.amountPln !== undefined) {
        acc.pln += row.amountPln;
      } else if (code === BASE_CURRENCY) {
        acc.pln += row.amount || 0;
      } else {
        acc.unconverted[code] =
          (acc.unconverted[code] || 0) + (row.amount || 0);
      }
      return acc;
    },
    { pln: 0, unconverted: {} },
  );

/** «300,00 USD · 50,00 EUR» для `unconverted`; пусто — пустая строка. */
export const formatUnconverted = (
  unconverted: Record<string, number>,
): string =>
  formatAmounts(
    Object.entries(unconverted).map(([currency, amount]) => ({
      currency,
      amount,
    })),
  );

/**
 * Разбивка по валютам с сервера (`LmsAmountDueType`: просрочка кандидата в
 * должники, мини-карточки) одной строкой «6500,00 PLN · 100,00 EUR»;
 * пусто — пустая строка (вызывающий решает, «—» или «0»).
 */
export const formatAmountsDue = (
  items: ReadonlyArray<{ amount?: number | null; currency?: string | null }>,
): string =>
  formatAmounts(
    items.map((item) => ({
      currency: codeOf(item.currency),
      amount: item.amount || 0,
    })),
  );

/**
 * Одно число для сортировки по разбивке с сервера: сумма PLN, у разбивки без
 * PLN — сумма первой строки (сервер ставит PLN первой, затем по сумме);
 * пусто — 0. Валюты не складываются: у группы с PLN её EUR в сортировку не
 * входит.
 */
export const primaryAmountOf = (
  items: ReadonlyArray<{ amount?: number | null; currency?: string | null }>,
): number => {
  const pln = items.find((item) => codeOf(item.currency) === BASE_CURRENCY);
  return (pln ?? items[0])?.amount || 0;
};

/**
 * Суммы по валютам списком отдельных строк («688 649,70 PLN»,
 * «9025,00 EUR»), PLN первой; пусто — «0,00 PLN». Для `MoneyTokens`:
 * каждая сумма — свой неразрывный токен, переносы только между ними.
 */
export const currencyTotalsList = (
  totals: Record<string, number>,
): string[] => {
  const items = orderAmounts(
    Object.entries(totals).map(([currency, amount]) => ({ currency, amount })),
  );
  return items.length
    ? items.map((item) => moneyNoWrap(item.amount, item.currency))
    : [moneyNoWrap(0, BASE_CURRENCY)];
};
